import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const FeaturedProductSection = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.2 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const features = [
    { icon: '⚡', label: 'Material Premium' },
    { icon: '🎨', label: 'Diseño Exclusivo' },
    { icon: '🏆', label: 'Edición Limitada' },
    { icon: '✅', label: 'Envío a Venezuela' },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-20 md:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050508 0%, #0a0510 50%, #050508 100%)' }}
      data-testid="featured-product"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #7c3aed, transparent)', transform: 'translate(-50%,-50%)' }} />
        <div className="absolute top-1/2 right-1/4 w-72 h-72 rounded-full opacity-8" style={{ background: 'radial-gradient(circle, #d4af37, transparent)', transform: 'translate(50%,-50%)' }} />
      </div>

      <div className="container mx-auto px-6 md:px-12">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-12">
          <div className="h-px flex-1 max-w-16" style={{ background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.5))' }} />
          <span className="text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full" style={{ color: '#d4af37', border: '1px solid rgba(212,175,55,0.3)', background: 'rgba(212,175,55,0.08)' }}>
            Producto Destacado
          </span>
          <div className="h-px flex-1" style={{ background: 'linear-gradient(to left, transparent, rgba(212,175,55,0.5))' }} />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side - with 3D frame effect */}
          <div
            className="relative"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0) rotateY(0deg)' : 'translateX(-40px) rotateY(-5deg)',
              transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {/* Outer glow frame */}
            <div className="absolute -inset-4 rounded-3xl opacity-60" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(124,58,237,0.2), rgba(212,175,55,0.2))', filter: 'blur(20px)' }} />

            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 z-20" style={{ borderTop: '2px solid #d4af37', borderLeft: '2px solid #d4af37', borderTopLeftRadius: '8px' }} />
            <div className="absolute top-0 right-0 w-8 h-8 z-20" style={{ borderTop: '2px solid #d4af37', borderRight: '2px solid #d4af37', borderTopRightRadius: '8px' }} />
            <div className="absolute bottom-0 left-0 w-8 h-8 z-20" style={{ borderBottom: '2px solid #d4af37', borderLeft: '2px solid #d4af37', borderBottomLeftRadius: '8px' }} />
            <div className="absolute bottom-0 right-0 w-8 h-8 z-20" style={{ borderBottom: '2px solid #d4af37', borderRight: '2px solid #d4af37', borderBottomRightRadius: '8px' }} />

            <div className="relative overflow-hidden rounded-2xl" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
              <img
                src="/uniform.jpg"
                alt="Uniforme Oficial Nirvana TCG"
                className="w-full h-auto object-cover"
                style={{ minHeight: '300px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_card-nexus-5/artifacts/0c42ban3_Imagen%20de%20WhatsApp%202025-05-01%20a%20las%2021.54.52_811d9dab.jpg";
                }}
              />
              {/* Image overlay shimmer */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 60%, rgba(212,175,55,0.1) 100%)' }} />

              {/* Badge */}
              <div className="absolute top-5 left-5">
                <span className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest" style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff', boxShadow: '0 4px 15px rgba(220,38,38,0.4)' }}>
                  NUEVO 2025
                </span>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
            }}
          >
            <h2 className="font-black mb-2 leading-tight" style={{
              fontFamily: 'Cinzel, serif',
              fontSize: 'clamp(2.5rem, 4vw, 4rem)',
              background: 'linear-gradient(135deg, #ffffff 0%, #d4af37 50%, #ffffff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Uniforme Oficial
            </h2>
            <h3 className="text-xl md:text-2xl font-bold mb-6" style={{ color: '#dc2626' }}>
              Nirvana TCG 2025
            </h3>

            <p className="text-gray-400 leading-relaxed mb-8" style={{ fontSize: '1.05rem' }}>
              Representa a tu comunidad con estilo. Nuestro uniforme oficial está diseñado
              para duelistas que quieren destacar en cada torneo. Material premium,
              diseño exclusivo y la calidad que mereces.
            </p>

            {/* Features grid */}
            <div className="grid grid-cols-2 gap-3 mb-10">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transition: `all 0.5s ease ${0.3 + i * 0.1}s`,
                  }}
                >
                  <span className="text-xl">{f.icon}</span>
                  <span className="text-sm font-semibold text-gray-300">{f.label}</span>
                </div>
              ))}
            </div>

            <Link
              to="/marketplace"
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-black text-sm uppercase tracking-wide transition-all hover:scale-105 hover:shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                color: '#fff',
                boxShadow: '0 8px 30px rgba(220,38,38,0.35)',
              }}
            >
              Ver en Tienda
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProductSection;
